import { isModifierKey } from "./isModifierKey.ts";
import { isSimplePrintableKey } from "./shared.ts";
import type { KeyCombination } from "./types.ts";

/**
 * Map of physical key codes to their display symbols.
 * Uses code (physical key) not key (character produced) for layout independence.
 */
const CODE_TO_DISPLAY: Record<string, string> = {
  // Letters (layout-independent - shows physical key position)
  KeyA: "A",
  KeyB: "B",
  KeyC: "C",
  KeyD: "D",
  KeyE: "E",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyI: "I",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  KeyM: "M",
  KeyN: "N",
  KeyO: "O",
  KeyP: "P",
  KeyQ: "Q",
  KeyR: "R",
  KeyS: "S",
  KeyT: "T",
  KeyU: "U",
  KeyV: "V",
  KeyW: "W",
  KeyX: "X",
  KeyY: "Y",
  KeyZ: "Z",

  // Digits
  Digit0: "0",
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",

  // Function keys
  F1: "F1",
  F2: "F2",
  F3: "F3",
  F4: "F4",
  F5: "F5",
  F6: "F6",
  F7: "F7",
  F8: "F8",
  F9: "F9",
  F10: "F10",
  F11: "F11",
  F12: "F12",
  F13: "F13",
  F14: "F14",
  F15: "F15",
  F16: "F16",
  F17: "F17",
  F18: "F18",
  F19: "F19",
  F20: "F20",

  // Navigation
  ArrowUp: "Arrow Up",
  ArrowDown: "Arrow Down",
  ArrowLeft: "Arrow Left",
  ArrowRight: "Arrow Right",
  Home: "Home",
  End: "End",
  PageUp: "Page Up",
  PageDown: "Page Down",

  // Editing
  Backspace: "Backspace",
  Delete: "Del",
  Insert: "Ins",

  // Whitespace & control
  Space: "Space",
  Enter: "Enter",
  NumpadEnter: "Num Enter",
  Tab: "Tab",
  Escape: "Esc",

  // Lock keys
  CapsLock: "Caps Lock",
  NumLock: "Num Lock",
  ScrollLock: "Scroll Lock",

  // Punctuation & symbols (US ANSI layout positions)
  Backquote: "`",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
  IntlBackslash: "§",

  // Numpad
  Numpad0: "Num0",
  Numpad1: "Num1",
  Numpad2: "Num2",
  Numpad3: "Num3",
  Numpad4: "Num4",
  Numpad5: "Num5",
  Numpad6: "Num6",
  Numpad7: "Num7",
  Numpad8: "Num8",
  Numpad9: "Num9",
  NumpadAdd: "Num+",
  NumpadSubtract: "Num-",
  NumpadMultiply: "Num*",
  NumpadDivide: "Num/",
  NumpadDecimal: "Num.",
  NumpadEqual: "Num=",
};

/**
 * Gets the display symbol for a physical key code.
 * Used as fallback when the key value isn't suitable for display.
 */
const getKeyDisplayFromCode = (code: string): string => {
  if (CODE_TO_DISPLAY[code]) {
    return CODE_TO_DISPLAY[code];
  }

  // Fallback for unmapped codes - extract readable part
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Numpad")) return code.slice(6);

  return code;
};

/** Set of codes that have special symbol displays (not regular characters) */
const SPECIAL_KEY_CODES = new Set([
  // Navigation
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
  // Editing
  "Backspace",
  "Delete",
  "Insert",
  // Whitespace & control
  "Space",
  "Enter",
  "NumpadEnter",
  "Tab",
  "Escape",
  // Lock keys
  "CapsLock",
  "NumLock",
  "ScrollLock",
  // Function keys
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F11",
  "F12",
  "F13",
  "F14",
  "F15",
  "F16",
  "F17",
  "F18",
  "F19",
  "F20",
  // Media
  "MediaPlayPause",
  "MediaStop",
  "MediaTrackNext",
  "MediaTrackPrevious",
  "AudioVolumeUp",
  "AudioVolumeDown",
  "AudioVolumeMute",
  // Misc
  "ContextMenu",
  "PrintScreen",
  "Pause",
  "Help",
]);

/**
 * Gets the display character for a key, respecting keyboard layout.
 * Uses layoutKey (from Keyboard Layout API) when available for accurate key-cap display,
 * otherwise falls back to key or code-based display.
 *
 * This handles:
 * - Dead keys (Option+E, Option+U, etc.) → shows base key from layoutKey or code
 * - Option+key combinations producing special chars → shows base key-cap
 * - Non-QWERTY keyboard layouts (Dvorak, AZERTY, etc.) → shows correct key-cap
 * - All special keys (arrows, function keys, etc.) → shows symbols
 */
export const getKeyDisplay = (combination: KeyCombination): string => {
  const { code, key, altKey, keyCap: layoutKey } = combination;

  // Special keys always use symbol display (arrows, function keys, etc.)
  if (SPECIAL_KEY_CODES.has(code)) {
    return CODE_TO_DISPLAY[code] ?? code;
  }

  // If we have layoutKey from the Keyboard Layout API, use it for accurate display.
  // This correctly handles all keyboard layouts (Dvorak, AZERTY, etc.)
  if (layoutKey && isSimplePrintableKey(layoutKey)) {
    return layoutKey.toUpperCase();
  }

  // Dead keys report key === "Dead" - fall back to code-based display
  if (key === "Dead") {
    return getKeyDisplayFromCode(code);
  }

  // When Option/Alt is pressed without layoutKey, use code-based display.
  // Option produces special characters (å, ∫, ç, etc.) but we want to display
  // "⌥A" not "⌥å" - the modifier symbol already indicates Option is held.
  if (altKey) {
    return getKeyDisplayFromCode(code);
  }

  // For simple printable ASCII characters, show the actual key-cap (layout-aware)
  if (isSimplePrintableKey(key)) {
    return key.toUpperCase();
  }

  // Fallback to code-based display for anything else
  return getKeyDisplayFromCode(code);
};

/**
 * Detects if the current platform is macOS.
 */
const isMacOS = (): boolean => {
  if (typeof navigator === "undefined") return false;

  // Modern API (when available)
  if ("userAgentData" in navigator) {
    const uaData = navigator.userAgentData as { platform?: string } | undefined;

    if (uaData?.platform) {
      return uaData.platform.toLowerCase() === "macos";
    }
  }

  // Fallback to userAgent parsing
  return /mac/i.test(navigator.userAgent);
};

/** macOS modifier symbols per Apple HIG */
const MAC_MODIFIERS = {
  ctrl: "⌃",
  alt: "⌥",
  shift: "⇧",
  meta: "⌘",
} as const;

/** Windows/Linux modifier labels */
const PC_MODIFIERS = {
  ctrl: "Ctrl",
  alt: "Alt",
  shift: "Shift",
  meta: "Win",
} as const;

/**
 * Formats a key combination for display using platform-appropriate modifier symbols.
 * - macOS: ⌃ ⌥ ⇧ ⌘ (Control, Option, Shift, Command) per Apple HIG
 * - Windows/Linux: Ctrl, Alt, Shift, Win
 *
 * The key display respects keyboard layout - shows what's on the user's key-cap.
 */
export const formatKeyCombination = (combination: KeyCombination): string[] => {
  const parts: string[] = [];
  const modifiers = isMacOS() ? MAC_MODIFIERS : PC_MODIFIERS;

  // Modifier order: Ctrl, Alt/Option, Shift, Meta/Command
  if (combination.ctrlKey) parts.push(modifiers.ctrl);
  if (combination.altKey) parts.push(modifiers.alt);
  if (combination.shiftKey) parts.push(modifiers.shift);
  if (combination.metaKey) parts.push(modifiers.meta);

  // Only add the key if it's not a pure modifier
  if (!isModifierKey(combination.code)) {
    const keyDisplay = getKeyDisplay(combination);
    parts.push(keyDisplay);
  }

  return parts;
};
